import type { EligibleSubsidy, SubsidyReference } from "@/features/subventions/types/subvention.types";
import { NO_UNIVERSE } from "@/features/subventions/utils/universeActions";
import { formatEuroWithDecimalOnlyIfNeeded } from "@/shared/utils/formatEuro";
import { formatPercentage } from "@/shared/utils/formatPercentatage";

// Pure helpers ported from the locked /subventions redesign mockup. The urgency thresholds,
// tones, tag classes and tier keys are kept verbatim because they drive sort + bucket order;
// only the user-facing strings branch on whether a date is a true expiry or a period reset.

const MS_PER_DAY = 86_400_000;

const URGENT_WITHIN_DAYS = 14;
const MONTH_WITHIN_DAYS = 31;

export type StatusTone = "ok" | "warn" | "depleted";
export type UrgencyOrigin = "expiry" | "reset";
export type TierKey = "urgent" | "month" | "later" | "none";

export type UrgencyTag = { cls: "urgent" | "soon", text: string };

export type SubsidyStatus = {
  tone: StatusTone
  tag: UrgencyTag | null
  date: Date | null
  origin: UrgencyOrigin | null
  expired: boolean
};

export type Tier = { key: TierKey, label: string, cls: string };

export type RemainingFigure = { primary: string | null, secondary: string | null };

export type RefBadge = { text: string, sub: string | null };

export type LimitCard = { period: string, cap: string, window: string | null };

export type SmallestCap = {
  period: string
  value: number
  periodFr: string
  money: boolean
};

const PERIOD_ORDER = ["monthly", "quarterly", "biannual", "yearly"] as const;
const PERIOD_FR: Record<string, string> = {
  monthly: "par mois", quarterly: "par trimestre", biannual: "par semestre", yearly: "par an",
};
const PERIOD_LABEL: Record<string, string> = {
  monthly: "Mensuel", quarterly: "Trimestriel", biannual: "Semestriel", yearly: "Annuel",
};

export const startOfToday = (now: Date = new Date()): Date =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate());

export const eur = (value: number): string => formatEuroWithDecimalOnlyIfNeeded(value);
export const qty = (value: number): string => `${value.toLocaleString("fr-FR")} art.`;

// Unicode emoji prefix matcher — ported from the mockup. FE0F (variation selector) and 200D
// (ZWJ) are written as escapes so they read as discrete class members; the arrow glyphs the
// mockup listed literally (2B05–2B07) already fall inside the 2B00–2BFF range.
// eslint-disable-next-line no-misleading-character-class -- VS16/ZWJ/skin-tone modifiers are intentional members so the matcher can consume a full leading emoji sequence
const EMOJI_RE = /^(?:[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{2190}-\u{21FF}\u{2300}-\u{23FF}\u{FE0F}\u{200D}\u{1F3FB}-\u{1F3FF}]+)\s*/u;

// Pick a single emoji out of a leading run, treating ZWJ sequences, flags, skin-tone and VS16
// modifiers as one cluster — so a policy named "🎬🎢 …" surfaces only its first icon, not both.
// Intl.Segmenter is typed via a local shape (the build's lib config omits it) and feature-detected
// at runtime, falling back to a code-point split where it is unavailable.
type GraphemeSegmenter = { segment: (input: string) => Iterable<{ segment: string }> };
const SegmenterClass = (Intl as {
  Segmenter?: new (
    locales?: string, options?: { granularity?: "grapheme" }
  ) => GraphemeSegmenter
}).Segmenter;
const GRAPHEMES = SegmenterClass ? new SegmenterClass(undefined, { granularity: "grapheme" }) : null;

const firstGrapheme = (run: string): string => {
  if (GRAPHEMES) {
    const { value } = GRAPHEMES.segment(run)[Symbol.iterator]().next();
    if (value) return value.segment;
  }
  return Array.from(run)[0] ?? run;
};

// `emoji` is the FIRST leading icon only; `text` is the name with the whole leading icon run
// stripped (so a title stays clean even when the source carries several icons).
export const splitEmoji = (name: string | null): { emoji: string | null, text: string } => {
  const match = (name ?? "").match(EMOJI_RE);
  if (!match) return { emoji: null, text: name ?? "" };
  return { emoji: firstGrapheme(match[0].trim()), text: (name ?? "").slice(match[0].length).trim() };
};

// A name with any leading icon run stripped — used for policy/envelope names, which surface no
// inline icon (the folder tile stands in for them). Falls back to the original if it is icon-only.
export const stripLeadingEmoji = (name: string): string => splitEmoji(name).text || name;

const UNIVERSE_FALLBACK_ICON = "🎁";

export const tileFor = (subsidy: EligibleSubsidy, universeIcon: (universe: string) => string | undefined): string => {
  const { emoji } = splitEmoji(subsidy.subsidy_perimeter_name);
  if (emoji) return emoji;
  const firstUniverse = (subsidy.universes ?? [])[0];
  return (firstUniverse && universeIcon(firstUniverse)) || UNIVERSE_FALLBACK_ICON;
};

export const displayName = (subsidy: EligibleSubsidy): string =>
  stripLeadingEmoji(subsidy.subsidy_perimeter_name);

export const isV1 = (name: string | null): boolean => /Règles migrées/i.test(name ?? "");

// The leaf's named envelope (subsidy_global_name) when it is a real surfaced parent: present,
// distinct from the perimeter itself, and not the hidden "Règles migrées (V1)" root. null
// otherwise. The single predicate the feed tree, the row chip and the drawer all share.
export const envelopeName = (subsidy: EligibleSubsidy): string | null => {
  const env = subsidy.subsidy_global_name;
  return env && env !== subsidy.subsidy_perimeter_name && !isV1(env) ? env : null;
};

export const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value + (value.length === 10 ? "T00:00:00" : ""));
  return Number.isNaN(date.getTime()) ? null : date;
};

const daysFrom = (date: Date, now: Date = startOfToday()): number =>
  Math.round((date.getTime() - now.getTime()) / MS_PER_DAY);

// fr-FR short date, e.g. "15 juin 2026" — matches the mockup's frDate. Neither shared
// formatDateToShort (no year) nor formatDateToLong (long month) matches, so this is bespoke.
export const formatDateShort = (date: Date): string =>
  date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

const periodEndDates = (subsidy: EligibleSubsidy): Date[] => {
  const currentPeriods = subsidy.perimeter_limits.current_periods ?? {};
  return Object.keys(currentPeriods)
    .map((key) => parseDate(currentPeriods[key]?.end_date))
    .filter((date): date is Date => date !== null);
};

// The earliest end across the advantage's current period windows, or null when it has none.
const earliestPeriodEnd = (subsidy: EligibleSubsidy): Date | null => {
  const ends = periodEndDates(subsidy);
  if (!ends.length) return null;
  return new Date(Math.min(...ends.map((d) => d.getTime())));
};

// The actionable deadline: the soonest of the hard expiry and the next period reset, so a
// monthly cap resetting before a year-end expiry still lands in the right urgency tier.
const urgencyDate = (subsidy: EligibleSubsidy): { date: Date | null, origin: UrgencyOrigin | null } => {
  const expiry = parseDate(subsidy.expiration_date);
  const reset = earliestPeriodEnd(subsidy);
  if (expiry && reset) {
    return reset.getTime() < expiry.getTime() ? { date: reset, origin: "reset" } : { date: expiry, origin: "expiry" };
  }
  if (expiry) return { date: expiry, origin: "expiry" };
  if (reset) return { date: reset, origin: "reset" };
  return { date: null, origin: null };
};

export const statusOf = (subsidy: EligibleSubsidy, now: Date = startOfToday()): SubsidyStatus => {
  const amount = subsidy.remaining_amount;
  const quantity = subsidy.remaining_quantity;
  // Depleted as soon as ANY present dimension is exhausted, so a dual-capped advantage with
  // money left but zero quantity (or the mirror) is "épuisé", matching bindingGauge, whose
  // bar empties on the binding (least-remaining) dimension.
  const depleted = amount === 0 || quantity === 0;

  const { date, origin } = urgencyDate(subsidy);

  if (depleted) return {
    tone: "depleted", tag: { cls: "urgent", text: "épuisé" }, date, origin, expired: false,
  };

  if (date) {
    const remaining = daysFrom(date, now);
    if (remaining < 0) return {
      tone: "depleted", tag: { cls: "urgent", text: "expiré" }, date, origin, expired: true,
    };
    if (remaining <= URGENT_WITHIN_DAYS) {
      return {
        tone: "warn", tag: { cls: "urgent", text: remaining <= 0 ? "aujourd'hui" : `dans ${remaining} j` }, date, origin, expired: false,
      };
    }
    if (remaining <= MONTH_WITHIN_DAYS) {
      return {
        tone: "warn", tag: { cls: "soon", text: `dans ${remaining} j` }, date, origin, expired: false,
      };
    }
  }
  return {
    tone: "ok", tag: null, date, origin, expired: false,
  };
};

// The small date line under a row's figure. Branches wording on origin so a period reset
// never reads as an expiry.
export const expiryLabel = (status: SubsidyStatus): string | null => {
  if (!status.date) return null;
  const date = formatDateShort(status.date);
  if (status.origin === "reset") return `se réinitialise le ${date}`;
  return status.expired ? `expiré le ${date}` : `jusqu'au ${date}`;
};

export const remainingFigure = (subsidy: EligibleSubsidy): RemainingFigure => {
  const amount = subsidy.remaining_amount;
  const quantity = subsidy.remaining_quantity;
  if (amount !== null && quantity !== null) return { primary: eur(amount), secondary: qty(quantity) };
  if (amount !== null) return { primary: eur(amount), secondary: null };
  if (quantity !== null) return { primary: qty(quantity), secondary: null };
  return { primary: null, secondary: null };
};

// Tier dividers. Labels stay origin-neutral (the urgent tier reads "À utiliser bientôt", not
// "Expire bientôt") because a bucket can mix expiry- and reset-dated subsidies. The keys are
// verbatim — they drive bucket order. An already-passed date falls to the "none" foot bucket
// rather than reading as urgent (the unit is depleted/expired and sinks there). tierOfDate is
// the date→tier core, shared so a feed unit (an envelope bucketed by its earliest descendant
// deadline) lands by the same thresholds.
export const tierOfDate = (date: Date | null, now: Date = startOfToday()): Tier => {
  if (!date) return { key: "none", label: "Sans échéance", cls: "tier-none" };
  const remaining = daysFrom(date, now);
  if (remaining < 0) return { key: "none", label: "Sans échéance", cls: "tier-none" };
  if (remaining <= URGENT_WITHIN_DAYS) return { key: "urgent", label: "À utiliser bientôt", cls: "tier-urgent" };
  if (remaining <= MONTH_WITHIN_DAYS) return { key: "month", label: "Ce mois-ci", cls: "tier-month" };
  return { key: "later", label: "Encore du temps", cls: "tier-later" };
};

export const TIER_ORDER: TierKey[] = ["urgent", "month", "later", "none"];

export const universeCounts = (list: EligibleSubsidy[]): Record<string, number> => {
  const counts: Record<string, number> = {};
  list.forEach((subsidy) => {
    const universes = subsidy.universes ?? [];
    if (!universes.length) counts[NO_UNIVERSE] = (counts[NO_UNIVERSE] ?? 0) + 1;
    universes.forEach((universe) => {
      counts[universe] = (counts[universe] ?? 0) + 1;
    });
  });
  return counts;
};

export const passesFilter = (subsidy: EligibleSubsidy, selected: Set<string>): boolean => {
  if (selected.size === 0) return true;
  const universes = subsidy.universes ?? [];
  if (!universes.length) return selected.has(NO_UNIVERSE);
  return universes.some((universe) => selected.has(universe));
};

export const refFigure = (reference: SubsidyReference): string => {
  if (reference.target_price !== null) return `prix cible ${eur(reference.target_price)}`;
  if (reference.fixed_amount !== null) return `${eur(reference.fixed_amount)} / art.`;
  if (reference.percentage !== null) return formatPercentage(reference.percentage);
  return "✓";
};

export const refBadge = (reference: SubsidyReference): RefBadge => {
  if (reference.target_price !== null) return { text: eur(reference.target_price), sub: "prix net" };
  if (reference.fixed_amount !== null) return { text: eur(reference.fixed_amount), sub: null };
  if (reference.percentage !== null) return { text: formatPercentage(reference.percentage), sub: null };
  return { text: "✓", sub: null };
};

// Descriptive blurbs for a reference. fixed_text is intentionally excluded (it duplicates the
// badge figure); ecommerces_texts are appended last — verbatim from the mockup.
export const refBlurbs = (reference: SubsidyReference): string[] => {
  const fields = [
    reference.description,
    reference.variable_text,
    reference.calculated_text,
    reference.remboursements_text,
    reference.billetteries_text,
    reference.campings_text,
    reference.parcs_text,
    reference.cinemas_text,
    reference.cadeaux_text,
  ].filter((text): text is string => Boolean(text));
  if (reference.ecommerces_texts?.length) return fields.concat(reference.ecommerces_texts);
  return fields;
};

type PeriodAmounts = Partial<Record<typeof PERIOD_ORDER[number], number>> | null | undefined;

const smallestOfPeriods = (source: PeriodAmounts, money: boolean): SmallestCap | null => {
  if (!source) return null;
  let best: SmallestCap | null = null;
  PERIOD_ORDER.forEach((period) => {
    const value = source[period];
    if (value !== undefined && (best === null || value < best.value)) {
      best = {
        period, value, periodFr: PERIOD_FR[period], money,
      };
    }
  });
  return best;
};

// Both binding caps of an advantage: the smallest-period amount cap and the smallest-period
// quantity cap, kept independent (no `amount ?? quantity` short-circuit) so a quantity-only
// exhaustion is not masked by a roomy amount cap. The amount cap falls back to the shared
// envelope limit (`global_limits`) when the advantage has no perimeter cap of its own — a 100%
// forfait drawing only on its envelope's budget still shows a gauge against that budget.
export const caps = (subsidy: EligibleSubsidy): SmallestCap[] => {
  const limits = subsidy.perimeter_limits;
  const amountSource = limits.amount ?? subsidy.global_limits.amount;
  return [
    smallestOfPeriods(amountSource, true),
    smallestOfPeriods(limits.quantity, false),
  ].filter((cap): cap is SmallestCap => cap !== null);
};

// Amount cap preferred, then quantity — the single figure shown next to a hero amount.
export const smallestCap = (subsidy: EligibleSubsidy): SmallestCap | null => {
  const list = caps(subsidy);
  return list.length ? list[0] : null;
};

export type Gauge = { pct: number, tone: StatusTone };

// The drawer's single bar reads REMAINING balance: full and green when nothing is spent, draining
// to amber then red as the binding dimension is consumed. The binding dimension is whichever cap
// has the least left, so the bar empties exactly when either amount or quantity is exhausted
// (agreeing with the "épuisé" tag). null when no cap applies or its remaining is unknown.
export const bindingGauge = (subsidy: EligibleSubsidy): Gauge | null => {
  const ratios = caps(subsidy).flatMap((cap) => {
    const remaining = cap.money ? subsidy.remaining_amount : subsidy.remaining_quantity;
    if (remaining === null) return [];
    return [cap.value > 0 ? Math.min(Math.max(remaining / cap.value, 0), 1) : 0];
  });
  if (!ratios.length) return null;
  const pct = Math.round(Math.min(...ratios) * 100);
  const tone: StatusTone = pct <= 0 ? "depleted" : pct < 25 ? "warn" : "ok";
  return { pct, tone };
};

// The earliest-period cap of an envelope's shared budget (its global amount limit). Drives the
// envelope header gauge. null when the envelope carries a remaining figure but no period limit.
export const earliestPeriodCap = (amountLimit: PeriodAmounts): { value: number, periodFr: string } | null => {
  const cap = smallestOfPeriods(amountLimit, true);
  return cap ? { value: cap.value, periodFr: cap.periodFr } : null;
};

export const resetDate = (subsidy: EligibleSubsidy): Date | null => {
  const end = earliestPeriodEnd(subsidy);
  if (!end) return null;
  const date = new Date(end.getTime());
  date.setDate(date.getDate() + 1);
  return date;
};

export const limitCards = (subsidy: EligibleSubsidy): LimitCard[] => {
  const limits = subsidy.perimeter_limits ?? null;
  const currentPeriods = limits?.current_periods ?? {};
  const cards: LimitCard[] = [];
  PERIOD_ORDER.forEach((period) => {
    const amount = limits?.amount?.[period];
    const quantity = limits?.quantity?.[period];
    if (amount === undefined && quantity === undefined) return;
    const cap = amount !== undefined && quantity !== undefined
      ? `${eur(amount)} · ${qty(quantity)}`
      : (amount !== undefined ? eur(amount) : qty(quantity as number));
    const periodWindow = currentPeriods[period];
    const start = parseDate(periodWindow?.start_date);
    const end = parseDate(periodWindow?.end_date);
    const window = start && end ? `${formatDateShort(start)} → ${formatDateShort(end)}` : null;
    cards.push({ period: PERIOD_LABEL[period], cap, window });
  });
  return cards;
};

// Beneficiary relationship → short role chip. Default "Ayant droit" (no hyphen), matching the
// mockup (distinct from the hyphenated "Ayant-droit" the legacy transform produces).
export const roleLabel = (relationship: string | null | undefined): string => {
  if (!relationship) return "Ayant droit";
  const head = String(relationship).split(" - ")[0].trim();
  const first = head.split(/\s+/)[0];
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
};

// Beneficiary display name: last token when multi-word; employee: first token. Matches the mockup.
export const firstNameToken = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  return parts[0] || name;
};
export const lastNameToken = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : name;
};
